import React from "react";
import * as MUI from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import SimpleAlert from "../../../../../../../components/display/SimpleAlert";
import { GlobalDataContext } from "../../../../../../../contexts/GlobalData";
import { EDIT_PRICE, DELETE_PRICE } from "../../../../../../../enums/PermissionTitles";
import { useGetWarehousePreferences } from "../../hooks/useGetWarehousePreferences";
import useGetServices from "./hooks/useGetServices";
import WarehouseServicesTable from "./components/WarehouseServicesTable";
import WarehouseServiceDetailsDialog from "./components/WarehouseServiceDetailsDialog";
import WarehouseServiceFormDialog from "./components/WarehouseServiceFormDialog";
import DeleteServiceDialog from "./components/DeleteServiceDialog";

const STRING_SORT_FIELDS = new Set(["title", "description"]);

// Client-side sort - Services has no backend paged/sorted endpoint (same
// reasoning as its client-side search above), so this mirrors what the
// server would do for Items: strings compared case-insensitively, numeric
// fields (duration/price/pdv/priceWithTax) compared numerically, and in
// both cases missing values sort last regardless of direction.
function compareServices(a, b, orderBy, order) {
  const direction = order === "desc" ? -1 : 1;
  const aValue = a?.[orderBy];
  const bValue = b?.[orderBy];
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  if (STRING_SORT_FIELDS.has(orderBy)) {
    return direction * String(aValue).localeCompare(String(bValue));
  }
  return direction * (Number(aValue) - Number(bValue));
}

// Warehouse tab content for the services/price-list catalog, rebuilt to
// match Artikli's table + details-dialog + form-dialog pattern (see
// WarehouseItemsTable.jsx, the source of truth this mirrors): search box
// above a table, row click opens a view-only details dialog, Edit/Delete
// icons in that dialog drive the form dialog / delete confirmation. The
// old always-expanded ServiceForm + SingleServiceFlip accordion list is
// gone - both are superseded by WarehouseServiceFormDialog and
// WarehouseServiceDetailsDialog respectively.
const WarehouseServicesTab = React.forwardRef(function WarehouseServicesTab(
  { clinicId },
  ref,
) {
  const { t } = useTranslation();
  const { data: prices, isLoading, error } = useGetServices(clinicId);
  const { data: warehousePreferences } = useGetWarehousePreferences(clinicId);
  const currency = warehousePreferences?.currency ?? "EUR";
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isEditingAllowed = isPermissionAllowed(EDIT_PRICE);
  const isDeletingAllowed = isPermissionAllowed(DELETE_PRICE);

  // Persisted to the URL (not local state) so search/sort survive refresh/
  // back-navigation and the filtered+sorted view is shareable - matches
  // WarehouseItemsTable.jsx's existing pattern (own param names, not the
  // Items tab's shared WAREHOUSE_TABLE_PARAMS, so switching tabs doesn't
  // leak one tab's search/sort into another's).
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("serviceSearch") || "";
  const sortParam = searchParams.get("serviceSort") || "";
  const [orderBy, order] = sortParam ? sortParam.split("=") : ["title", "asc"];

  function setSearch(value) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) {
        params.set("serviceSearch", value);
      } else {
        params.delete("serviceSearch");
      }
      return params;
    });
  }

  const [detailsServiceId, setDetailsServiceId] = React.useState(null);
  const [formState, setFormState] = React.useState(null); // { service: null | service }
  const [servicePendingDelete, setServicePendingDelete] = React.useState(null);

  const services = React.useMemo(() => (Array.isArray(prices) ? prices : []), [prices]);

  // No backend search for this catalog-sized list, same as
  // Categories/Suppliers - filtering the already-fetched array client-side
  // is proportionate rather than adding a new endpoint.
  const filteredServices = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return services;
    return services.filter((service) => service.title?.toLowerCase().includes(query));
  }, [services, search]);

  const sortedServices = React.useMemo(
    () => [...filteredServices].sort((a, b) => compareServices(a, b, orderBy, order)),
    [filteredServices, orderBy, order],
  );

  function handleSort(field, direction) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("serviceSort", `${field}=${direction}`);
      return params;
    });
  }

  const detailsService = services.find((service) => service.id === detailsServiceId) ?? null;

  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => setFormState({ service: null }),
  }));

  function closeServiceForm() {
    setFormState(null);
  }

  if (error) {
    return <SimpleAlert message="ErrorFetchingWarehouseItemsData" severity="error" />;
  }

  return (
    <MUI.Stack spacing={3}>
      <MUI.Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: "divider" }}>
        <MUI.TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("SearchServices")}
          size="small"
          fullWidth
          inputProps={{ "data-testid": "warehouse.services.search-input" }}
          InputProps={{
            startAdornment: (
              <MUI.InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </MUI.InputAdornment>
            ),
          }}
        />
      </MUI.Paper>

      <WarehouseServicesTable
        items={sortedServices}
        currency={currency}
        isLoading={isLoading}
        emptyMessage={search ? "NoSearchResults" : "NoServices"}
        onRowClick={(service) => setDetailsServiceId(service.id)}
        order={order}
        orderBy={orderBy}
        onSort={handleSort}
      />

      <WarehouseServiceDetailsDialog
        service={detailsService}
        currency={currency}
        canEdit={isEditingAllowed}
        canDelete={isDeletingAllowed}
        onClose={() => setDetailsServiceId(null)}
        onEdit={(service) => {
          setDetailsServiceId(null);
          setFormState({ service });
        }}
        onDelete={(service) => setServicePendingDelete(service)}
      />

      {formState && (
        <WarehouseServiceFormDialog
          open={Boolean(formState)}
          onClose={closeServiceForm}
          clinicId={clinicId}
          service={formState.service}
        />
      )}

      <DeleteServiceDialog
        service={servicePendingDelete}
        clinicId={clinicId}
        onCancel={() => setServicePendingDelete(null)}
        onDeleted={() => setDetailsServiceId(null)}
      />
    </MUI.Stack>
  );
});

export default WarehouseServicesTab;
